import { useEffect, useState } from "react";
import { downloadFile, formatDateTime } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useTheme } from "@hooks/useTheme";
import { DocumentIcon, DownloadIcon, TrashIcon } from "../Icons";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "@configs/runtimeConfig"
import { PaginationEllipsis } from "@components/Pagination";

const getDownloadLink = (fileInfoSeq) => `/download/${fileInfoSeq}`;

const DocumentTable = ({
  data,
  onDelete,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  itemsPerPage = 5,
  totalData,
  hasDeleteColumn = true, // Add prop to show Delete column
  maxPagesToShow = 3,
}) => {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const commitPage = () => {
    if (pageInput === "") {
      handlePageChange(1);
      setPageInput("1");
      return;
    }

    let page = Number(pageInput);

    if (!Number.isInteger(page)) {
      setPageInput(String(currentPage));
      return;
    }

    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;

    handlePageChange(page);
    setPageInput(String(page));
  };

  const handlePageChange = (page) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handleDownload = (document) => {
    // Download document using utility
    downloadFile(document.realFileName || document.serverFileName, getDownloadLink(document.fileInfoSeq));
  };

  const handleDelete = (document) => {
    if (onDelete) {
      onDelete(document);
    }
  };

  const getPaginationRange = () => {
    const pages = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    const delta = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, currentPage - delta);
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - maxPagesToShow + 1;
    }

    if (startPage > 1) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      pages.push("...");
    }

    return pages;
  };

  return (
    <div className="relative">
      <div className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-y-1.5">
            <thead className={colors.bgTableBody}>
              <tr className={`rounded-lg ${colors.bgTableStripe} transition-colors`}>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium tracking-wider whitespace-nowrap uppercase ${colors.textSecondary} rounded-l-lg`}
                >
                  {t("common.file-name")}
                </th>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium tracking-wider whitespace-nowrap uppercase ${colors.textSecondary}`}
                >
                  {t("common.uploaded-by")}
                </th>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium tracking-wider whitespace-nowrap uppercase ${colors.textSecondary}`}
                >
                  {t("common.uploaded-time")}
                </th>
                <th
                  className={`px-4 py-3 text-right text-xs font-medium tracking-wider whitespace-nowrap uppercase ${colors.textSecondary} rounded-r-lg`}
                >
                  {t("common.action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index} className={`${colors.bgDocumentTable} transition-colors`}>
                  <td className={`px-4 py-2 text-sm ${colors.textPrimary} rounded-l-lg whitespace-nowrap`}>
                    <div className="flex items-center space-x-3">
                      <DocumentIcon className="h-5 w-5" />
                      <span className={`text-sm font-medium ${colors.textPrimary}`}>
                        {row.realFileName || row.serverFileName || "Unknown"}
                      </span>
                    </div>
                  </td>
                  <td className={`px-4 py-2 text-sm ${colors.textPrimary} whitespace-nowrap`}>
                    <UserNameWithId name={row.createrName} id={row.createrId} />
                  </td>
                  <td className={`px-4 py-2 text-sm ${colors.textPrimary} whitespace-nowrap`}>
                    {row.createdAt ? formatDateTime(row.createdAt) : "Unknown"}
                  </td>
                  <td className={`px-4 py-2 text-sm ${colors.textPrimary} rounded-r-lg whitespace-nowrap`}>
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDownload(row);
                        }}
                        className={`inline-flex items-center px-3 py-1 text-xs font-medium ${colors.textActive} rounded hover:opacity-80`}
                      >
                        <DownloadIcon className="mr-1 h-3 w-3" />
                        {t("common.download")}
                      </button>
                      {hasDeleteColumn && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row);
                          }}
                          className={`p-1 transition-colors ${colors.iconDelete} hover:opacity-80`}
                          title={t("common.delete")}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className={`${colors.bgTableBody} px-4 py-3`}>
        <div className="flex flex-row items-center justify-between gap-4 flex-nowrap">
          {/* Go to page */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 text-sm">
              <span className={`text-xs font-medium ${colors.textSubTitle}`}>{t("common.table.go-to-page")}</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={pageInput}
                onChange={(e) => {
                  setPageInput(e.target.value);
                }}
                onBlur={commitPage}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitPage();
                    e.target.blur();
                  }
                }}
                className={`h-8 w-16 rounded border px-2 py-1 text-sm font-medium focus:outline-none ${colors.borderPaginationButton} ${colors.bgInput} ${colors.textPrimary}`}
              />

              {/* Showing info */}
              <div className="flex items-center text-sm">
                <span className={`${colors.textSubTitle} text-xs font-medium`}>
                  {(() => {
                    const startItem = ((currentPage - 1) * itemsPerPage) + 1;
                    const endItem = Math.min(currentPage * itemsPerPage, totalData);
                    const currentLanguage = i18n.language || 'ko';

                    if (currentLanguage === 'ko') {
                      return (
                        <span>
                          총 <span className={colors.textPrimary}>{totalData}</span>개 중 <span className={colors.textPrimary}>{startItem} - {endItem}</span>개 표시 중
                        </span>
                      );
                    } else {
                      return (
                        <span>
                          {t("common.table.showing")} <span className={colors.textPrimary}>{startItem} - {endItem}</span> {t("common.table.of")} <span className={colors.textPrimary}>{totalData}</span>
                        </span>
                      );
                    }
                  })()}
                </span>
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {/* Previous Page Button */}
              <button
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
                title="Previous page"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                <span className={`text-xs font-medium ${colors.textActive}`}>{t("common.table.prev")}</span>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPaginationRange().map((page, index) => (
                  <div key={index}>
                    {page === "..." ? (
                      <PaginationEllipsis className={`inline-flex items-center justify-center rounded border px-3 py-1 text-sm ${colors.borderPaginationButton} ${colors.textPrimary} ${colors.bgInput}`} />
                    ) : (
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`rounded border px-3 py-1 text-sm transition-colors ${colors.borderPaginationButton} ${colors.textPrimary} ${
                          currentPage === page ? colors.bgPaginationButtonActive : colors.bgInput
                        }`}
                      >
                        {page}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Next Page Button */}
              <button
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
                title="Next page"
              >
                <span className={`text-xs font-medium ${colors.textActive}`}>{t("common.table.next")}</span>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentTable;
